'use client';

/* =============================================================================
 * Homepage "Google Reviews" section — REAL data via Places API (New). See docs/86.
 * -----------------------------------------------------------------------------
 * Fed server-side by `fetchGoogleReviews()` and passed in as props through
 * home-page.tsx (the API key never reaches the browser).
 *
 * Compliance (docs/86 §8, option b) — three guardrails baked in here:
 *   1. The aggregate shown is Google's TRUE all-in rating + count (includes
 *      every 1★) — never a recomputed/inflated figure.
 *   2. A "view all on Google" link to the full, unfiltered review set.
 *   3. A "featured / sourced from Google" disclosure (honest labelling).
 * Featured cards are filtered to 4–5★ upstream in `adaptPlaceToReviewsData`.
 *
 * Do NOT reintroduce hardcoded/mock reviews — that was DP1 (false advertising).
 * ===========================================================================*/

import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import type { GoogleReviewsData, HomepageReview } from '@/types/reviews';

function ReviewAvatar({ review }: { review: HomepageReview }) {
  const initials = review.authorName
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  if (!review.authorPhotoUrl) {
    return (
      <div className="w-14 h-14 rounded-full bg-[#801515]/10 text-[#801515] flex items-center justify-center font-semibold text-lg ring-2 ring-[#801515]/20">
        {initials || '★'}
      </div>
    );
  }
  return (
    <Image
      src={review.authorPhotoUrl}
      alt={review.authorName}
      width={56}
      height={56}
      className="w-14 h-14 rounded-full object-cover ring-2 ring-[#801515]/20"
    />
  );
}

function GoogleReviewsCarousel({ reviews }: { reviews: HomepageReview[] }) {
  const t = useTranslations('design.homepage.reviews');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'next' | 'prev'>('next');

  const goToNext = useCallback(() => {
    if (isAnimating) return;
    setDirection('next');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
      setTimeout(() => setIsAnimating(false), 50);
    }, 400);
  }, [isAnimating, reviews.length]);

  const goToPrev = () => {
    if (isAnimating) return;
    setDirection('prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
      setTimeout(() => setIsAnimating(false), 50);
    }, 400);
  };

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentIndex) return;
    setDirection(index > currentIndex ? 'next' : 'prev');
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setTimeout(() => setIsAnimating(false), 50);
    }, 400);
  };

  // Auto-advance every 6 seconds
  useEffect(() => {
    const timer = setInterval(goToNext, 6000);
    return () => clearInterval(timer);
  }, [goToNext]);

  const review = reviews[currentIndex];

  return (
    <div className="relative max-w-5xl mx-auto">
      {/* Main review card */}
      <div className="bg-white rounded-3xl shadow-2xl px-20 md:px-24 lg:px-28 py-8 md:py-12 lg:py-16 mx-4 relative observe-me fade-in overflow-hidden">
        {/* Google logo watermark */}
        <div className="absolute top-6 right-6 opacity-10">
          <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/>
          </svg>
        </div>

        {/* Review content wrapper with animations */}
        <div
          key={currentIndex}
          className={`transition-all duration-500 ease-out ${
            isAnimating
              ? direction === 'next'
                ? 'opacity-0 -translate-x-12'
                : 'opacity-0 translate-x-12'
              : 'opacity-100 translate-x-0'
          }`}
        >
          {/* Star rating */}
          <div className="flex gap-1 mb-6 justify-center">
            {[...Array(review.rating)].map((_, i) => (
              <svg
                key={i}
                className="w-8 h-8 text-[#801515] fill-current transition-all duration-300"
                viewBox="0 0 20 20"
                style={{
                  transitionDelay: isAnimating ? '0ms' : `${i * 50}ms`,
                  opacity: isAnimating ? 0 : 1,
                  transform: isAnimating ? 'scale(0.8)' : 'scale(1)'
                }}
              >
                <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
              </svg>
            ))}
          </div>

          {/* Review text */}
          <blockquote className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-8 text-center font-light italic">
            &ldquo;{review.text}&rdquo;
          </blockquote>

          {/* Author info */}
          <div className="flex items-center justify-center gap-4">
            <ReviewAvatar review={review} />
            <div>
              <p className="font-semibold text-gray-900 text-lg">{review.authorName}</p>
              {review.relativeTime && (
                <p className="text-gray-500 text-sm">{review.relativeTime}</p>
              )}
            </div>
          </div>
        </div>

        {/* Navigation arrows */}
        <button
          onClick={goToPrev}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border-2 border-[#801515] shadow-lg hover:shadow-xl flex items-center justify-center text-[#801515] hover:bg-[#801515] hover:text-white transition-all duration-300 transform hover:scale-110"
          aria-label={t('previousReview')}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white border-2 border-[#801515] shadow-lg hover:shadow-xl flex items-center justify-center text-[#801515] hover:bg-[#801515] hover:text-white transition-all duration-300 transform hover:scale-110"
          aria-label={t('nextReview')}
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {/* Dots navigation */}
      <div className="flex justify-center gap-3 mt-8">
        {reviews.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex
                ? 'w-12 h-3 bg-[#801515]'
                : 'w-3 h-3 bg-gray-300 hover:bg-[#801515]/50'
            }`}
            aria-label={t('goToReview', { index: index + 1 })}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The homepage reviews section (heading + true aggregate + carousel + Google link).
 * Render only when `data` has at least one featured review (home-page.tsx guards too).
 */
export function HomepageReviews({ data }: { data: GoogleReviewsData }) {
  const t = useTranslations('design.homepage');

  if (!data || data.reviews.length === 0) return null;

  return (
    <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white via-[#faf8f6] to-white overflow-hidden mt-8">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 observe-me slide-up">
          <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
            <span className="text-[#801515]">{t('reviews.heading')}</span>
            <br />
            <span className="text-gray-800 text-xl sm:text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
              {t('reviews.subheading')}
            </span>
          </h2>
          <div className="w-16 h-1 bg-[#801515] mx-auto mb-8" />
          {/* True, all-in aggregate (includes every 1★) — never recomputed */}
          <div className="flex items-center justify-center gap-3 text-gray-600">
            <span className="text-4xl font-bold text-[#801515]">{data.rating.toFixed(1)}</span>
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-6 h-6 text-[#801515] fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <span className="text-lg">{t('reviews.basedOn', { count: data.userRatingCount })}</span>
          </div>
          {/* Compliance: honest "featured / sourced from Google" disclosure (docs/86 §8) */}
          <p className="text-gray-400 text-sm mt-3">{t('reviews.sourcedFromGoogle')}</p>
        </div>

        <GoogleReviewsCarousel reviews={data.reviews} />

        {/* Compliance: link to the full, unfiltered review set on Google (docs/86 §8) */}
        <div className="text-center mt-10">
          <a
            href={data.googleMapsUri}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#801515] hover:text-[#601010] font-medium underline underline-offset-4 transition-colors"
          >
            {t('reviews.viewAllOnGoogle', { count: data.userRatingCount })}
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-32 right-10 w-2 h-2 bg-[#801515] rounded-full opacity-40" style={{ animation: 'pulse 4s ease-in-out infinite' }} />
      <div className="absolute bottom-20 left-16 w-3 h-3 bg-[#478B8B] rounded-full opacity-35" style={{ animation: 'pulse 5s ease-in-out infinite', animationDelay: '1s' }} />
    </section>
  );
}
